// Permission modules and actions configuration
export const PERMISSIONS = {
  MODULES: {
    BRANCH_MANAGEMENT: "Branch_Management",
    REDEEM_OFFERS: "Redeem_Offers",
    PARTNER_MANAGEMENT: "Partner_Management",
    DEALS_MANAGEMENT: "Deals_Management",
    DEALS_ANALYTICS: "Deals_Analytics",
    SUBSCRIPTION: "Subscription",
    ROLES_PERMISSION: "Roles_Permission",
  },

  ACTIONS: {
    REDEEM_OFFERS: "Redeem_Offers",
    REDEEM_EDIT: "Redeem_Edit",
    REDEEM_DELETE: "Redeem_Delete",

    VIEW_PARTNERS: "View_Partners",
    CREATE_PARTNER: "Create_Partner",
    EDIT_PARTNER: "Edit_Partner",
    DELETE_PARTNER: "Delete_Partner",

    VIEW_BRANCHES: "View_Branches",
    CREATE_BRANCH: "Create_Branch",
    EDIT_BRANCH: "Edit_Branch",
    DELETE_BRANCH: "Delete_Branch",

    CREATE_OFFER: "Create_Offer",
    EDIT_OFFER: "Edit_Offer",
    DELETE_OFFER: "Delete_Offer",
    BLAST_DEAL: "Blast_Deal",
    VIEW_DEALS: "View_Deals",

    VIEW_REPORTS_ANALYTICS: "View_Reports_Analytics",

    VIEW_SUBSCRIPTION: "View_Subscription",
    PURCHASE_SUBSCRIPTION: "Purchase_Subscription",
    VIEW_SUBSCRIPTION_HISTORY: "View_Subscription_History",

    ROLE_VIEW: "Role_View",
    ROLE_CREATE: "Role_Create",
    ROLE_EDIT: "Role_Edit",
    ROLE_DELETE: "Role_Delete",
  },
};


export const transformApiPermissions = (apiPermissions) => {
  if (!apiPermissions || typeof apiPermissions !== 'object') {
    return {};
  }

  const transformed = {};
  
  Object.keys(apiPermissions).forEach(module => {
    const modulePermissions = apiPermissions[module];
    if (Array.isArray(modulePermissions)) {
      transformed[module] = modulePermissions.map(perm => perm.name);
    }
  });

  return transformed;
};

export const getPermissionSlugs = (apiPermissions, module, actionNames = []) => {
  if (!apiPermissions || !apiPermissions[module]) {
    return [];
  }

  const modulePermissions = apiPermissions[module];
  return modulePermissions
    .filter(perm => actionNames.includes(perm.name))
    .map(perm => perm.permission_slug);
};

export const hasApiPermission = (apiPermissions, isMainVendor, module, action) => {
  if (isMainVendor) return true;

  if (!apiPermissions || !apiPermissions[module]) {
    return false;
  }

  const modulePermissions = apiPermissions[module];
  return modulePermissions.some(perm => perm.name === action);
};

//* Route permissions mapping
export const ROUTE_PERMISSIONS = {
  "/offers": {
    module: PERMISSIONS.MODULES.DEALS_MANAGEMENT,
    action: PERMISSIONS.ACTIONS.VIEW_DEALS,
  },
  "/offers/new": {
    module: PERMISSIONS.MODULES.DEALS_MANAGEMENT,
    action: PERMISSIONS.ACTIONS.CREATE_OFFER,
  },
  "/offers/edit/*": {
    module: PERMISSIONS.MODULES.DEALS_MANAGEMENT,
    action: PERMISSIONS.ACTIONS.EDIT_OFFER,
  },
  "/inventory": {
    module: PERMISSIONS.MODULES.DEALS_MANAGEMENT,
    action: PERMISSIONS.ACTIONS.EDIT_OFFER,
  },
  "/partners": {
    module: PERMISSIONS.MODULES.PARTNER_MANAGEMENT,
    action: PERMISSIONS.ACTIONS.VIEW_PARTNERS,
  },
  "/add-partner": {
    module: PERMISSIONS.MODULES.PARTNER_MANAGEMENT,
    action: PERMISSIONS.ACTIONS.CREATE_PARTNER,
  },
  "/edit-partner/*": {
    module: PERMISSIONS.MODULES.PARTNER_MANAGEMENT,
    action: PERMISSIONS.ACTIONS.EDIT_PARTNER,
  },
  "/deal/campaigns/blast/*": {
    module: PERMISSIONS.MODULES.DEALS_MANAGEMENT,
    action: PERMISSIONS.ACTIONS.BLAST_DEAL,
  },
  "/roles": {
    module: PERMISSIONS.MODULES.ROLES_PERMISSION,
    action: PERMISSIONS.ACTIONS.ROLE_VIEW,
  },
  "/roles/manage": {
    module: PERMISSIONS.MODULES.ROLES_PERMISSION,
    action: PERMISSIONS.ACTIONS.ROLE_VIEW,
  },
  "/roles/create": {
    module: PERMISSIONS.MODULES.ROLES_PERMISSION,
    action: PERMISSIONS.ACTIONS.ROLE_CREATE,
  },
  "/roles/edit/*": {
    module: PERMISSIONS.MODULES.ROLES_PERMISSION,
    action: PERMISSIONS.ACTIONS.ROLE_EDIT,
  },
  "/roles/team-member/create": {
    module: PERMISSIONS.MODULES.ROLES_PERMISSION,
    action: PERMISSIONS.ACTIONS.ROLE_CREATE,
  },
  "/roles/team-member/edit/*": {
    module: PERMISSIONS.MODULES.ROLES_PERMISSION,
    action: PERMISSIONS.ACTIONS.ROLE_EDIT,
  },
  "/pricing": {
    module: PERMISSIONS.MODULES.SUBSCRIPTION,
    action: PERMISSIONS.ACTIONS.VIEW_SUBSCRIPTION,
  },
  "/subscription": {
    module: PERMISSIONS.MODULES.SUBSCRIPTION,
    action: PERMISSIONS.ACTIONS.VIEW_SUBSCRIPTION,
  },
  "/payment-history": {
    module: PERMISSIONS.MODULES.SUBSCRIPTION,
    action: PERMISSIONS.ACTIONS.VIEW_SUBSCRIPTION_HISTORY,
  },
  "/reports": {
    module: PERMISSIONS.MODULES.DEALS_ANALYTICS,
    action: PERMISSIONS.ACTIONS.VIEW_REPORTS_ANALYTICS,
  },
};

//* Default role permissions - matching TeamSlice structure
export const DEFAULT_ROLE_PERMISSIONS = {
  admin: {
    [PERMISSIONS.MODULES.REDEEM_OFFERS]: [
      PERMISSIONS.ACTIONS.REDEEM_OFFERS,
      PERMISSIONS.ACTIONS.REDEEM_EDIT,
      PERMISSIONS.ACTIONS.REDEEM_DELETE,
    ],
    [PERMISSIONS.MODULES.PARTNER_MANAGEMENT]: [
      PERMISSIONS.ACTIONS.VIEW_PARTNERS,
      PERMISSIONS.ACTIONS.CREATE_PARTNER,
      PERMISSIONS.ACTIONS.EDIT_PARTNER,
      PERMISSIONS.ACTIONS.DELETE_PARTNER,
    ],
    [PERMISSIONS.MODULES.DEALS_MANAGEMENT]: [
      PERMISSIONS.ACTIONS.CREATE_OFFER,
      PERMISSIONS.ACTIONS.EDIT_OFFER,
      PERMISSIONS.ACTIONS.DELETE_OFFER,
      PERMISSIONS.ACTIONS.BLAST_DEAL,
      PERMISSIONS.ACTIONS.VIEW_DEALS,
    ],
    [PERMISSIONS.MODULES.DEALS_ANALYTICS]: [
      PERMISSIONS.ACTIONS.VIEW_REPORTS_ANALYTICS,
    ],
    [PERMISSIONS.MODULES.SUBSCRIPTION]: [
      PERMISSIONS.ACTIONS.VIEW_SUBSCRIPTION,
      PERMISSIONS.ACTIONS.PURCHASE_SUBSCRIPTION,
      PERMISSIONS.ACTIONS.VIEW_SUBSCRIPTION_HISTORY,
    ],
    [PERMISSIONS.MODULES.ROLES_PERMISSION]: [
      PERMISSIONS.ACTIONS.ROLE_VIEW,
      PERMISSIONS.ACTIONS.ROLE_CREATE,
      PERMISSIONS.ACTIONS.ROLE_EDIT,
      PERMISSIONS.ACTIONS.ROLE_DELETE,
    ],
  },

  manager: {
    [PERMISSIONS.MODULES.REDEEM_OFFERS]: [PERMISSIONS.ACTIONS.REDEEM_OFFERS],
    [PERMISSIONS.MODULES.PARTNER_MANAGEMENT]: [
      PERMISSIONS.ACTIONS.VIEW_PARTNERS,
      PERMISSIONS.ACTIONS.CREATE_PARTNER,
      PERMISSIONS.ACTIONS.EDIT_PARTNER,
      PERMISSIONS.ACTIONS.DELETE_PARTNER,
    ],
    [PERMISSIONS.MODULES.DEALS_MANAGEMENT]: [
      PERMISSIONS.ACTIONS.BLAST_DEAL,
      PERMISSIONS.ACTIONS.VIEW_DEALS,
    ],
    [PERMISSIONS.MODULES.DEALS_ANALYTICS]: [
      PERMISSIONS.ACTIONS.VIEW_REPORTS_ANALYTICS,
    ],
    [PERMISSIONS.MODULES.ROLES_PERMISSION]: [
      PERMISSIONS.ACTIONS.ROLE_VIEW,
      PERMISSIONS.ACTIONS.ROLE_CREATE,
      PERMISSIONS.ACTIONS.ROLE_EDIT,
      PERMISSIONS.ACTIONS.ROLE_DELETE,
    ],
  },

  employee: {
    [PERMISSIONS.MODULES.REDEEM_OFFERS]: [PERMISSIONS.ACTIONS.REDEEM_OFFERS],
    [PERMISSIONS.MODULES.DEALS_ANALYTICS]: [
      PERMISSIONS.ACTIONS.VIEW_REPORTS_ANALYTICS,
    ],
    [PERMISSIONS.MODULES.ROLES_PERMISSION]: [
      PERMISSIONS.ACTIONS.ROLE_VIEW,
      PERMISSIONS.ACTIONS.ROLE_CREATE,
      PERMISSIONS.ACTIONS.ROLE_EDIT,
      PERMISSIONS.ACTIONS.ROLE_DELETE,
    ],
  },
};

//* Permission helpers => Get route permission
export const getRoutePermission = (pathname) => {
  if (ROUTE_PERMISSIONS[pathname]) {
    return ROUTE_PERMISSIONS[pathname];
  }

  //* Check wildcard matches
  for (const [route, permission] of Object.entries(ROUTE_PERMISSIONS)) {
    if (route.includes("*")) {
      const baseRoute = route.replace("/*", "");
      if (pathname.startsWith(baseRoute)) {
        return permission;
      }
    }
  }

  return null;
};

export const hasRoutePermission = (pathname, userPermissions) => {
  const routePermission = getRoutePermission(pathname);
  if (!routePermission) return true;

  const { module, action } = routePermission;
  const modulePermissions = userPermissions[module] || [];
  return modulePermissions.includes(action);
};

//* Hardcoded fallback permissions (matching API format)
export const FALLBACK_PERMISSIONS = {
  "Redeem_Offers": ["Redeem_Offers", "Redeem_Edit", "Redeem_Delete"],
  "Partner_Management": [
    "View_Partners",
    "Create_Partner",
    "Edit_Partner",
    "Delete_Partner",
  ],
  "Deals_Management": [
    "Create_Offer",
    "Edit_Offer",
    "Delete_Offer",
    "Blast_Deal",
    "View_Deals",
  ],
  "Deals_Analytics": ["View_Reports_Analytics"],
  "Subscription": [
    "View_Subscription",
    "Purchase_Subscription",
    "View_Subscription_History",
  ],
  "Roles_Permission": ["Role_View", "Role_Create", "Role_Edit", "Role_Delete"],
};

//* Helper function to get permissions (API first, fallback if error)
export const getSystemPermissions = async () => {
  try {
    const { getSystemPermissions } = await import(
      "../requests/vendor/RolesRequest"
    );
    const response = await getSystemPermissions();

    if (response.success) {
      return response.data;
    } else {
      console.warn("API failed, using fallback permissions:", response.message);
      return FALLBACK_PERMISSIONS;
    }
  } catch (error) {
    console.warn("Error fetching permissions from API, using fallback:", error);
    return FALLBACK_PERMISSIONS;
  }
};

export const hasPermission = (
  userPermissions,
  isMainVendor,
  module,
  action
) => {
  if (isMainVendor) return true;

  if (!userPermissions) return false;

  if (Array.isArray(userPermissions)) {
    return userPermissions.includes(action);
  }
  if (userPermissions[module] && Array.isArray(userPermissions[module])) {
    const modulePermissions = userPermissions[module];    
    if (modulePermissions.length > 0 && typeof modulePermissions[0] === 'object' && modulePermissions[0].name) {
      return modulePermissions.some(perm => perm.name === action);
    }
    
    // Old format - array of strings
    return modulePermissions.includes(action);
  }

  return false;
};

//* Check if user has any of the specified actions
export const hasAnyPermission = (
  userPermissions,
  isMainVendor,
  module,
  actions = []
) => {
  if (isMainVendor) return true;
  
  return actions.some(action => 
    hasPermission(userPermissions, isMainVendor, module, action)
  );
};

//* Check if user has all of the specified actions
export const hasAllPermissions = (
  userPermissions,
  isMainVendor,
  module,
  actions = []
) => {
  if (isMainVendor) return true;
  
  return actions.every(action => 
    hasPermission(userPermissions, isMainVendor, module, action)
  );
};
